import type { ReactNode } from "react";
import { BookOpen, Inbox, Mail, Phone, PhoneForwarded, PhoneIncoming } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Agent } from "@/types/agent/agent";
import { LISTENER_PHONE_MODE } from "@/types/phone/listener";

export const MUTED_INDICATOR_COLOR = "#9CA3AF";

interface GenieChannelIndicatorsProps {
  genie: Agent;
  brand: string;
  /** Smaller chips for tight spaces such as table cells. */
  compact?: boolean;
  /** Also show a chip when the genie has knowledge base documents. */
  showKnowledge?: boolean;
  /** Leave the mailbox out, for layouts that show the address on its own row. */
  hideMailbox?: boolean;
  /** Extra chips (built with IndicatorChip) rendered before the channel chips. */
  leading?: ReactNode;
  className?: string;
}

interface IndicatorChipProps {
  label: string;
  color: string;
  compact: boolean;
  tooltip: ReactNode;
  children: ReactNode;
}

export const IndicatorChip = ({ label, color, compact, tooltip, children }: IndicatorChipProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        aria-label={label}
        className={cn(
          "inline-flex items-center gap-1 rounded-full font-medium ring-1 whitespace-nowrap",
          compact ? "h-6 px-1.5 text-[11px]" : "h-7 px-2.5 text-xs",
        )}
        style={{ color, backgroundColor: `${color}10`, ["--tw-ring-color" as string]: `${color}40` }}
      >
        {children}
      </span>
    </TooltipTrigger>
    <TooltipContent>{tooltip}</TooltipContent>
  </Tooltip>
);

/** A genie's live channels: working mailbox, own phone number (answered or listened to) and escalation numbers. */
export const getGenieChannels = (genie: Agent) => {
  const mailboxAddress = genie.mailbox?.email_address ?? null;
  const mailboxStatus = mailboxAddress ? genie.mailbox?.status : undefined;
  const phoneNumber = genie.phone?.number ?? null;

  return {
    mailboxAddress,
    mailboxStatus,
    hasMailbox: mailboxStatus === "provisioned" || mailboxStatus === "suspended",
    phoneNumber,
    isListening: Boolean(phoneNumber) && genie.phone?.metadata?.mode === LISTENER_PHONE_MODE,
    escalationNumbers: (genie.escalation_phones ?? (genie.escalation_phone ? [genie.escalation_phone] : []))
      .map((phone) => phone.number)
      .filter((number): number is string => Boolean(number)),
  };
};

/**
 * At-a-glance channels for a genie: its mailbox, an inbound number it answers,
 * a listener number it records, and the escalation numbers it hands callers to.
 * Renders nothing when the genie has none of them.
 */
export const GenieChannelIndicators = ({
  genie,
  brand,
  compact = false,
  showKnowledge = false,
  hideMailbox = false,
  leading,
  className,
}: GenieChannelIndicatorsProps) => {
  const knowledgeCount = showKnowledge ? (genie.settings?.conversation_config?.agent?.prompt?.knowledge_base?.length ?? 0) : 0;
  const channels = getGenieChannels(genie);
  const { mailboxAddress, mailboxStatus, phoneNumber, isListening, escalationNumbers } = channels;
  const hasMailbox = channels.hasMailbox && !hideMailbox;

  if (!leading && !hasMailbox && !phoneNumber && escalationNumbers.length === 0 && knowledgeCount === 0) return null;

  const iconClass = compact ? "w-3 h-3" : "w-3.5 h-3.5";
  const listensToEmail = genie.mailbox?.mode === "listen";
  const mailboxLabel =
    mailboxStatus === "suspended"
      ? `Mailbox paused · ${mailboxAddress}`
      : listensToEmail
        ? `Listens to email forwarded to ${mailboxAddress}`
        : `Answers email at ${mailboxAddress}`;

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
        {leading}

        {hasMailbox && (
          <IndicatorChip
            label={mailboxLabel}
            color={mailboxStatus === "suspended" ? MUTED_INDICATOR_COLOR : brand}
            compact={compact}
            tooltip={mailboxLabel}
          >
            {listensToEmail ? <Inbox className={iconClass} /> : <Mail className={iconClass} />}
          </IndicatorChip>
        )}

        {phoneNumber && !isListening && (
          <IndicatorChip
            label={`Answers calls on ${phoneNumber}`}
            color={brand}
            compact={compact}
            tooltip={`Answers calls on ${phoneNumber}`}
          >
            <Phone className={iconClass} />
          </IndicatorChip>
        )}

        {phoneNumber && isListening && (
          <IndicatorChip
            label={`Listens to and records calls on ${phoneNumber}`}
            color={brand}
            compact={compact}
            tooltip={`Listens to and records calls on ${phoneNumber}`}
          >
            <span className="relative inline-flex">
              <PhoneIncoming className={iconClass} />
              <span className="absolute -top-1 -right-1 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500 ring-1 ring-white" />
              </span>
            </span>
          </IndicatorChip>
        )}

        {escalationNumbers.length > 0 && (
          <IndicatorChip
            label={`Escalates calls to ${escalationNumbers.join(", ")}`}
            color={brand}
            compact={compact}
            tooltip={
              <div className="space-y-0.5">
                <p className="font-medium">Escalates calls to</p>
                {escalationNumbers.map((number) => (
                  <p key={number}>{number}</p>
                ))}
              </div>
            }
          >
            <PhoneForwarded className={iconClass} />
          </IndicatorChip>
        )}

        {knowledgeCount > 0 && (
          <IndicatorChip
            label={`Knowledge base · ${knowledgeCount} document${knowledgeCount === 1 ? "" : "s"}`}
            color={brand}
            compact={compact}
            tooltip={`Knowledge base · ${knowledgeCount} document${knowledgeCount === 1 ? "" : "s"}`}
          >
            <BookOpen className={iconClass} />
          </IndicatorChip>
        )}
      </div>
    </TooltipProvider>
  );
};
